import { User } from 'lucide-react';

import { ALVO_OPTIONS, type ServiceName } from '@/api/observability';
import { Input } from '@/components/ui/input';
import { SearchInput } from '@/components/ui/search-input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { PageSizeSelect } from './PageSizeSelect';
import { PeriodSelect, type PeriodOption } from './PeriodSelect';

/* ------------------------------------------------------------------ */
/*  Types & constants                                                  */
/* ------------------------------------------------------------------ */

export type Period = '15m' | '1h' | '6h' | '24h' | '7d';

// Observability não inclui 30d (janela menor que a auditoria); por isso
// passa um array próprio para o PeriodSelect via prop `options`.
export const PERIOD_OPTIONS: PeriodOption<Period>[] = [
  { value: '15m', label: '15 min' },
  { value: '1h', label: 'Última hora' },
  { value: '6h', label: '6 horas' },
  { value: '24h', label: '24 horas' },
  { value: '7d', label: '7 dias' },
];

export const LIMIT_OPTIONS = [50, 100, 200, 500] as const;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface ObservabilityFilterBarProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  period: Period;
  onPeriodChange: (value: Period) => void;
  limit: number;
  onLimitChange: (value: number) => void;
  limitLabel?: string;
  userFilter?: string;
  onUserFilterChange?: (value: string) => void;
  errorsOnly?: boolean;
  onErrorsOnlyChange?: (value: boolean) => void;
  serviceFilter?: ServiceName | 'all';
  onServiceFilterChange?: (value: ServiceName | 'all') => void;
  alvoFilter?: string;
  onAlvoFilterChange?: (value: string) => void;
  operationFilter?: string;
  onOperationFilterChange?: (value: string) => void;
}

export function ObservabilityFilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'reqId, endpoint...',
  period,
  onPeriodChange,
  limit,
  onLimitChange,
  limitLabel = 'registros',
  userFilter,
  onUserFilterChange,
  errorsOnly,
  onErrorsOnlyChange,
  serviceFilter,
  onServiceFilterChange,
  alvoFilter,
  onAlvoFilterChange,
  operationFilter,
  onOperationFilterChange,
}: ObservabilityFilterBarProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
      {/* Busca */}
      {onSearchChange != null && (
        <div className="flex-1 space-y-1">
          <Label className="text-xs font-medium">Buscar</Label>
          <SearchInput
            value={search ?? ''}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
          />
        </div>
      )}

      {/* Filtro por usuário */}
      {onUserFilterChange != null && (
        <div className="space-y-1">
          <Label className="text-xs font-medium">Usuário</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={userFilter ?? ''}
              onChange={(e) => onUserFilterChange(e.target.value)}
              placeholder="E-mail, nome ou CPF..."
              className="w-full pl-9 sm:w-[260px]"
            />
          </div>
        </div>
      )}

      {/* Serviço */}
      {onServiceFilterChange != null && (
        <div className="space-y-1">
          <Label className="text-xs font-medium">Serviço</Label>
          <Select
            value={serviceFilter ?? 'all'}
            onValueChange={(v) => onServiceFilterChange(v as ServiceName | 'all')}
          >
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="jurisapp">JurisApp</SelectItem>
              <SelectItem value="autenticador">Autenticador</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Alvo */}
      {onAlvoFilterChange != null && (
        <div className="space-y-1">
          <Label className="text-xs font-medium">Alvo</Label>
          <Select
            value={alvoFilter || 'all'}
            onValueChange={(v) => onAlvoFilterChange(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-full sm:w-[160px]">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {ALVO_OPTIONS.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Operação */}
      {onOperationFilterChange != null && (
        <div className="space-y-1">
          <Label className="text-xs font-medium">Operação</Label>
          <Input
            value={operationFilter ?? ''}
            onChange={(e) => onOperationFilterChange(e.target.value)}
            placeholder="ex: peticionar"
            className="w-full sm:w-[160px]"
          />
        </div>
      )}

      {/* Período (sem 30d — janela menor que auditoria) */}
      <PeriodSelect<Period>
        value={period}
        onChange={onPeriodChange}
        options={PERIOD_OPTIONS}
        width="sm:w-[140px]"
      />

      {/* Limite */}
      <PageSizeSelect
        value={limit}
        onChange={onLimitChange}
        options={LIMIT_OPTIONS}
        width="sm:w-[150px]"
        label="Limite"
        itemLabel={limitLabel}
      />

      {/* Switch: apenas com erros */}
      {onErrorsOnlyChange != null && (
        <label className="flex cursor-pointer items-center gap-2 pb-0.5">
          <Switch checked={errorsOnly ?? false} onCheckedChange={onErrorsOnlyChange} />
          <span className="whitespace-nowrap text-sm">Apenas com erros</span>
        </label>
      )}
    </div>
  );
}
